import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v2';

export default function KYCVerification() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/kyc/status`);
      const data = await res.json();
      if (data.success) {
        setStatus(data.status);
        if (data.status === 'verified') {
          if (pollRef.current) clearInterval(pollRef.current);

          try {
            const stored = localStorage.getItem('user');
            if (stored) {
              const u = JSON.parse(stored);
              u.idVerified = true;
              u.id_verified = true;
              localStorage.setItem('user', JSON.stringify(u));
            }
          } catch (e) {
            console.error('Failed to update local user KYC state', e);
          }

          setTimeout(() => navigate('/dashboard'), 2000);
        }
      }
    } catch (err) {
      setError('Failed to fetch verification status.');
      setStatus('not_started');
    }
  }, [authFetch, navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // While a verification is pending or under manual review, the decision
  // arrives asynchronously via webhook (Veriff's redirect-back page carries
  // no decision info -- see Veriff Redirection Guide). Poll periodically so
  // the user sees an updated status without manually refreshing.
  useEffect(() => {
    if (status === 'pending' || status === 'manual_review') {
      pollRef.current = setInterval(fetchStatus, 8000);
      return () => clearInterval(pollRef.current);
    }
  }, [status, fetchStatus]);

  // Stripe.js is only needed if a session response ever returns client_secret
  // (legacy/disabled-by-reality fallback). Loaded lazily, not on every visit,
  // so Veriff users (the current default) never pay for an unused script load.
  const ensureStripeLoaded = () => {
    return new Promise((resolve) => {
      if (window.Stripe) { setStripeLoaded(true); resolve(); return; }
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = () => { setStripeLoaded(true); resolve(); };
      document.head.appendChild(script);
    });
  };

  const startVerification = async () => {
  setStarting(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/kyc/session`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create session');

      if (data.status === 'verified') {
        setStatus('verified');
        return;
      }

      // Provider-neutral branch: redirect_url (Veriff hosted flow) takes
      // priority. client_secret (Stripe embedded flow) is the legacy path,
      // kept for backward compatibility but not the active default provider.
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }

      if (data.client_secret) {
        await ensureStripeLoaded();
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        const { error: stripeError } = await stripe.verifyIdentity(data.client_secret);

        if (stripeError) {
          setError(stripeError.message || 'Verification cancelled or failed.');
          setStatus('requires_input');
        } else {
          setStatus('pending');
          setError('');
        }
        return;
      }

      throw new Error('Verification session response was missing required data.');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const styles = {
    page: { minHeight: '100vh', background: '#f8f9fa', fontFamily: 'Arial, sans-serif' },
    container: { maxWidth: '560px', margin: '0 auto', padding: '40px 20px' },
    card: { background: '#fff', borderRadius: '12px', padding: '40px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
    icon: { fontSize: '48px', textAlign: 'center', marginBottom: '16px' },
    title: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e', textAlign: 'center', marginBottom: '8px' },
    subtitle: { fontSize: '15px', color: '#666', textAlign: 'center', marginBottom: '32px', lineHeight: '1.5' },
    stepList: { listStyle: 'none', padding: 0, margin: '0 0 32px 0' },
    step: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' },
    stepNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    stepText: { fontSize: '14px', color: '#444', lineHeight: '1.5' },
    btn: { width: '100%', padding: '14px', background: '#2563ea', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
    btnDisabled: { width: '100%', padding: '14px', background: '#93c5fd', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'not-allowed' },
    btnSkip: { width: '100%', padding: '12px', background: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', marginTop: '12px' },
    error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px', marginBottom: '20px' },
    success: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', color: '#16a34a', fontSize: '14px', marginBottom: '20px', textAlign: 'center' },
    pending: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px', color: '#92400e', fontSize: '14px', textAlign: 'center', lineHeight: '1.6' },
    fintracNote: { fontSize: '12px', color: '#999', textAlign: 'center', marginTop: '20px', lineHeight: '1.5' },
  };

  const renderContent = () => {
    if (status === 'loading') {
      return <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Checking verification status...</div>;
    }

    if (status === 'verified') {
      return (
        <div style={styles.card}>
          <div style={styles.icon}>✅</div>
          <div style={styles.title}>Identity Verified</div>
          <div style={{...styles.success, marginTop: '16px'}}>Your identity has been verified. Redirecting to dashboard...</div>
        </div>
      );
    }

    if (status === 'pending' || status === 'submitted') {
      return (
        <div style={styles.card}>
          <div style={styles.icon}>⏳</div>
          <div style={styles.title}>Verification In Progress</div>
          <div style={styles.pending}>
            Your documents have been submitted and are under review. This usually takes a few minutes. You will be notified once verification is complete.
          </div>
          <button style={styles.btnSkip} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      );
    }

    if (status === 'manual_review') {
      return (
        <div style={styles.card}>
          <div style={styles.icon}>🔍</div>
          <div style={styles.title}>Verification Under Review</div>
          <div style={styles.pending}>
            Your verification is under review. No action is needed right now — we'll email you as soon as there's an update.
          </div>
          <button style={styles.btnSkip} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      );
    }

    if (status === 'failed' || status === 'requires_input') {
      return (
        <div style={styles.card}>
          <div style={styles.icon}>⚠️</div>
          <div style={styles.title}>Verification Unsuccessful</div>
          <div style={styles.error}>
            We weren't able to verify your identity with the documents provided. Please try again with a clear, valid government-issued ID.
          </div>
          <button
            style={starting ? styles.btnDisabled : styles.btn}
            onClick={startVerification}
            disabled={starting}
          >
            {starting ? 'Starting...' : 'Try Again'}
          </button>
          <button style={styles.btnSkip} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      );
    }

    return (
      <div style={styles.card}>
        <div style={styles.icon}>🪪</div>
        <div style={styles.title}>Verify Your Identity</div>
        <div style={styles.subtitle}>
          ZoomPay is required by Canadian law (FINTRAC) to verify the identity of all customers before processing transactions.
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <ul style={styles.stepList}>
          <li style={styles.step}>
            <span style={styles.stepNum}>1</span>
            <span style={styles.stepText}><strong>Prepare your ID</strong> — passport, driver's licence, or government-issued photo ID</span>
          </li>
          <li style={styles.step}>
            <span style={styles.stepNum}>2</span>
            <span style={styles.stepText}><strong>Take a photo</strong> — use your phone or webcam to capture the front of your document</span>
          </li>
          <li style={styles.step}>
            <span style={styles.stepNum}>3</span>
            <span style={styles.stepText}><strong>Take a selfie</strong> — a quick liveness check to confirm it's you</span>
          </li>
        </ul>

        <button
          style={starting ? styles.btnDisabled : styles.btn}
          onClick={startVerification}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Start Identity Verification'}
        </button>

        <button style={styles.btnSkip} onClick={() => navigate('/dashboard')}>
          Do this later
        </button>

        <p style={styles.fintracNote}>
          Your information is encrypted and processed securely by our identity verification partner. ZoomPay stores only the verification result and document type as required by FINTRAC regulations.
        </p>
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <Navigation />
      <div style={styles.container}>{renderContent()}</div>
    </div>
  );
}
